import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import Popup from "../../../../components/Popup/Popup";
import RequiredLabel from "../../../../components/RequiredLabel/RequiredLabel";
import { useSession } from "../../../../contexts/SessionContext";
import { ProjectSubjectResourceType } from "../../../../models/ProjectSubjectResource";
import translate from "../../../../services/Translate";
import { createProjectSubjectResource, updateProjectSubjectResource } from "../../ProjectSubject.function";
import { EditProjectSubjectResourceModalProps } from "./EditProjectRessources.d";

const EditProjectSubjectResourceModal = ({
  show,
  onHide,
  subjectId,
  resource,
  onSaved,
}: EditProjectSubjectResourceModalProps) => {
  const { language } = useSession();

  const _getEmptyForm = () => ({
    name: "",
    url: "",
    type: null as ProjectSubjectResourceType | null,
  });

  const [form, setForm] = useState(_getEmptyForm);

  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    if (resource) {
      setForm({
        name: resource.name ?? "",
        url: resource.url ?? "",
        type: resource.type as ProjectSubjectResourceType,
      });
    } else {
      setForm(_getEmptyForm());
    }
  }, [resource, show]);

  const _handleChange = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const _onSave = () => {
    setIsSaving(true);

    const payload = {
      name: form.name,
      url: form.url || null,
      type: form.type,
    };

    const callback = (saved: any) => {
      onSaved(saved);
      onHide();
    };

    if (resource) {
      updateProjectSubjectResource(subjectId, resource.id, payload, callback, language, () => setIsSaving(false));
    } else {
      createProjectSubjectResource(subjectId, payload, callback, language, () => setIsSaving(false));
    }
  };

  return (
    <Popup
      show={show}
      onClose={() => onHide()}
      modalTitle={translate(
        language,
        resource
          ? "PAGES.PROJECT_SUBJECT.EDIT.RESSOURCES.TITLE_EDIT"
          : "PAGES.PROJECT_SUBJECT.EDIT.RESSOURCES.TITLE_ADD"
      )}
      footerContent={
        <React.Fragment>
          <DstButton variant="secondary" value={translate(language, "GLOBAL.CANCEL")} clickFunction={() => onHide()} />
          <DstButton
            value={translate(language, "GLOBAL.SAVE")}
            disabled={isSaving || !form.name || !form.type}
            clickFunction={_onSave}
          />
        </React.Fragment>
      }
    >
      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "GLOBAL.NAME")} />
        </Form.Label>
        <Form.Control value={form.name} onChange={(event) => _handleChange("name", event.target.value)} />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{translate(language, "PAGES.PROJECT_SUBJECT.EDIT.RESSOURCES.URL")}</Form.Label>
        <Form.Control value={form.url} onChange={(event) => _handleChange("url", event.target.value)} />
      </Form.Group>

      <Form.Group>
        <Form.Label>
          <RequiredLabel label={translate(language, "GLOBAL.TYPE")} />
        </Form.Label>
        <Form.Select
          value={form.type ?? ""}
          onChange={(event) =>
            _handleChange("type", event.target.value ? (event.target.value as ProjectSubjectResourceType) : null)
          }
        >
          <option value="" disabled>
            {translate(language, "PAGES.PROJECT_SUBJECT.EDIT.RESSOURCES.SELECT")}
          </option>

          {Object.values(ProjectSubjectResourceType).map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
    </Popup>
  );
};

export default EditProjectSubjectResourceModal;
